import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { getSnapshot } from 'mobx-state-tree';
import { useSnackbar } from 'notistack';
import { Box, Fade } from '@material-ui/core';
import SyncIcon from '@material-ui/icons/Sync';
import { useAppContext } from 'contexts/AppContext';
import { isSuccessState, wait } from 'globals/utils';
import defaultLocales from 'globals/locales/default';
import FsPendingButton from 'ui-elements/buttons/FsPendingButton';
import FsPaper from 'ui-elements/main-elements/FsPaper';
import FsDefaultFallbackComponent from 'ui-elements/loaders/FsDefaultFallbackComponent';
import UserDetailsSingleRow from './UserDetailsSingleRow';
import { UserDetailsSingleRowDataProps } from './UserDetailsSingleRow/UserDetailsSingleRow';
import { getIsValid } from './FsUserDetailsForm.utils';

export interface FsUserDetailsFormProps {
  title: string;
}

const FsUserDetailsForm: React.FC<FsUserDetailsFormProps> = ({ title }) => {
  const { enqueueSnackbar } = useSnackbar();
  const {
    userDetails: {
      status,
      data: userDetails,
      getUserDetailsArray,
      init: initStore,
      setUserDetails,
      update: updateStore,
    },
  } = useAppContext();
  const rows = getUserDetailsArray();
  const [pending, setPending] = useState<boolean>(false);

  const handleChange = ({ name, value }: UserDetailsSingleRowDataProps) => {
    setUserDetails({
      [name]: value,
    });
  };

  const handleUpdate = async () => {
    setPending(true);
    // eslint-disable-next-line no-console
    console.log('UPDATE:', getSnapshot(userDetails));
    await wait(1000);
    setPending(false);
    updateStore();
    enqueueSnackbar(defaultLocales.userDetailsUpdateNotificationSuccess, {
      variant: 'success',
    });
  };

  const isValid = getIsValid(rows);

  useEffect(() => {
    initStore();
  }, [initStore]);

  if (!isSuccessState(status))
    return (
      <FsDefaultFallbackComponent
        status={status}
        data-test="default-fallback-component"
      />
    );

  return (
    <Fade in timeout={1000}>
      <FsPaper title={title}>
        <Box maxWidth={600}>
          {rows.map(({ name, value }) => (
            <UserDetailsSingleRow
              key={name}
              {...{ name, value }}
              onChange={(changedField) => handleChange(changedField)}
            />
          ))}
          <FsPendingButton
            pending={pending}
            onClick={handleUpdate}
            startIcon={<SyncIcon />}
            disabled={!isValid}
          >
            {defaultLocales.update}
          </FsPendingButton>
        </Box>
      </FsPaper>
    </Fade>
  );
};

export default observer(FsUserDetailsForm);
